/**
 * API Configuration
 * Centralizes all API URL configuration for the application
 */

// Import shared API endpoints and constants
import { ENDPOINTS, API_VERSION_V2 } from '@roi/shared/constants/api-endpoints';

// Detect static deployments like Netlify or GitHub Pages
export const isStaticDeployment = () => {
  // Check environment variable first (more reliable)
  if (process.env.REACT_APP_USE_MOCK_DATA === 'true') {
    console.log('Static deployment detected via environment variable');
    return true;
  }
  
  // Fallback to hostname detection
  const isNetlify = window.location.hostname.includes('netlify.app');
  const isGitHubPages = window.location.hostname.includes('github.io');
  
  if (isNetlify || isGitHubPages) {
    console.log('Static deployment detected via hostname:', window.location.hostname);
    return true;
  }
  
  return false;
};

// Base URL for the API should use relative paths when in development (for proxy)
// In production, use the configured URL or default to relative path
let baseUrl = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || '')
  : '';  // In development, use relative path for proxy

// Ensure API_BASE_URL always ends with /api (but avoid double /api/api)
if (baseUrl && !baseUrl.endsWith('/api')) {
  // Remove trailing slash if present before adding /api
  baseUrl = baseUrl.replace(/\/$/, '') + '/api';
} else if (!baseUrl) {
  // If baseUrl is empty, just use /api
  baseUrl = '/api';
}

export const API_BASE_URL = baseUrl;

// Helper function to determine if we should use mock data
export function shouldUseMockData(): boolean {
  return isStaticDeployment();
}

// Helper function to create mock API responses with consistent format
export function mockApiResponse<T>(data: T): T {
  return data;
}
// No additional API_PREFIX needed as it's already included in the API_BASE_URL

// IMPORTANT: Auth endpoints do NOT use the v2 version (they use /api/auth/login directly)
// but import endpoints DO use the v2 version (/api/v2/imports/...)

// Re-export shared endpoints
export { ENDPOINTS, API_VERSION_V2 };

/**
 * Build a full API URL from the base URL and endpoint
 * With proxy configuration, we can use simple relative URLs
 *
 * For example:
 * - buildApiUrl('auth/login') returns '/api/auth/login'
 * - buildApiUrl('v2/reports') returns '/api/v2/reports'
 */
export function buildApiUrl(endpoint: string): string {
  // Ensure we don't have double slashes by removing leading slash from endpoint if it exists
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;

  // Simple construction for proxy - all requests start with /api
  // If we already have api in the URL (from BASE_URL), don't add it again
  const apiPrefix = API_BASE_URL.endsWith('/api') ? API_BASE_URL : '/api';
  
  // Special handling for endpoints that already include api/ to avoid /api/api/
  if (cleanEndpoint.startsWith('api/')) {
    const url = `/${cleanEndpoint}`; // Just add a leading slash
    console.log(`Building API URL (already has api prefix): ${url}`);
    return url;
  }
  
  const url = `${apiPrefix}/${cleanEndpoint}`;
  console.log(`Building API URL: ${url}`);
  return url;
}

/**
 * Get authentication headers from local storage
 */
export function getAuthHeaders(): { Authorization: string } | Record<string, never> {
  // Fix: Get 'authToken' instead of 'token' to match the authentication system
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default {
  API_BASE_URL,
  ENDPOINTS,
  buildApiUrl,
  getAuthHeaders,
};